import { CubePresenter, FlipIntent, Logger } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'
import { AnaliseFormKeys } from 'src/modules/motor-credito/analise/form'
import { AnaliseSelectKeys } from 'src/modules/motor-credito/analise/select'
import GrupoKeys from '../grupo.keys'
import grupoService from '../grupo.service'
import type { FaixaRatingRow, GrupoRow } from '../grupo.types'
import { GrupoFormKeys } from './grupo_form.keys'
import GrupoFormScope from './grupo_form.scopes'

const LOG = Logger.get('GrupoFormPresenter')

export class GrupoFormPresenter extends CubePresenter<MainPresenter, GrupoFormScope> {
    // :: Constructor
    public constructor(app: MainPresenter) {
        super(app, new GrupoFormScope())
    }

    // :: Instance
    #grupoId?: number

    // :: Methods

    buscarGrupoSelecionado = async () => {
        if (!this.#grupoId) return

        const grupo = await grupoService.fetchGrupoInfo(this.#grupoId)
        if (grupo) {
            this.scope.idSelected = grupo.id!
            this.scope.tipo = grupo.tipo
            this.scope.nome = grupo.nome
            this.scope.status = grupo.status
            this.scope.rating1Start = grupo.rating1Start ?? 0
            this.scope.rating2Start = grupo.rating2Start ?? 0
            this.scope.faixasRating1 = grupo.faixasRating1
            this.scope.faixasRating2 = grupo.faixasRating2
            this.scope.analises = grupo.analises ?? []
        }
    }

    public override release() {
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(
        intent: FlipIntent,
        initialization: boolean,
        last: boolean
    ): Promise<boolean> {
        const keys = new GrupoFormKeys(this.app, intent)

        keys.parentSlot(this.scope)

        LOG.debug(`applyParameters ${keys.grupoId}`)
        if (initialization) {
            await this.#initialize()
        }

        if (this.#grupoId !== keys.grupoId || (last && keys.addAnaliseId)) {
            this.#grupoId = keys.grupoId

            if (keys.addAnaliseId && this.#grupoId) {
                await grupoService.addGrupoAnalise(this.#grupoId, keys.addAnaliseId)
                this.app.notify('success', 'Adicionado com sucesso')
            }

            await this.buscarGrupoSelecionado()
        }

        if (last) {
            keys.dialogSlot(null)
            keys.addAnaliseId = undefined
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new GrupoFormKeys(this.app, intent)
        keys.grupoId = this.#grupoId
        LOG.debug(`publishParameters ${keys.grupoId}`)
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onClose = this.action(this.handleClose)

        this.scope.onSave = this.action(this.handleSave)

        this.scope.onDeleteFaixaRating = this.action(this.handleDeleteFaixaRating)

        this.scope.onSaveFaixaRating1 = this.action(this.handleSaveFaixaRating1)
        this.scope.onClearFaixaRating1 = this.action(this.handleClearFaixaRating1)
        this.scope.onEditFaixaRating1 = this.action(this.handleEditFaixaRating1)

        this.scope.onSaveFaixaRating2 = this.action(this.handleSaveFaixaRating2)
        this.scope.onClearFaixaRating2 = this.action(this.handleClearFaixaRating2)
        this.scope.onEditFaixaRating2 = this.action(this.handleEditFaixaRating2)

        this.scope.onCreateAnalise = this.action(this.handleCreateAnalise)
        this.scope.onEditAnalise = this.action(this.handleEditAnalise)
        this.scope.onAddAnalise = this.action(this.handleAddAnalise)
        this.scope.onDeleteAnalise = this.action(this.handleDeleteAnalise)
    }

    private async handleClose() {
        await new GrupoKeys(this.app).flip()
    }

    private async handleSave(): Promise<GrupoRow | undefined> {
        try {
            const grupoSave = {
                tipo: this.scope.tipo as 'BORDERO',
                nome: this.scope.nome,
                status: this.scope.status as 'ATIVO' | 'INATIVO',
                rating1Start: this.scope.rating1Start,
                rating2Start: this.scope.rating2Start,
                faixasRating1: [],
                faixasRating2: []
            }

            const retSalvar =
                this.scope.idSelected > 0
                    ? await grupoService.updateGrupo(this.scope.idSelected, grupoSave)
                    : await grupoService.saveGrupo({
                          ...grupoSave,
                          faixasRating1: this.scope.faixasRating1,
                          faixasRating2: this.scope.faixasRating2
                      })

            if (retSalvar) {
                const target = new GrupoFormKeys(this.app)
                target.grupoId = retSalvar.id!

                await target.flip()
                this.app.notify('success', 'Salvo com sucesso')
            }

            return retSalvar
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        }
    }

    private async handleEditFaixaRating1(id: number) {
        const frEdit = this.scope.faixasRating1.find((fr) => Number(fr.id) === id)
        if (!frEdit) {
            await this.handleClearFaixaRating1()
            this.app.notify('error', 'Faixa Rating 1 que está tentando editar não encontrada')
            return
        }
        this.scope.editingFaixaRating1Id = frEdit.id
        this.scope.editingFaixaRating1Tipo = frEdit.tipo
        this.scope.editingFaixaRating1Sigla = frEdit.sigla
        this.scope.editingFaixaRating1Descricao = frEdit.descricao ?? ''
        this.scope.editingFaixaRating1FaixaInicial = frEdit.faixaInicial ?? 0
        this.scope.editingFaixaRating1FaixaFinal = frEdit.faixaFinal ?? 0
        this.scope.editingFaixaRating1MostrarRating = frEdit.mostrarRating
    }

    private async handleEditFaixaRating2(id: number) {
        const frEdit = this.scope.faixasRating2.find((fr) => Number(fr.id) === id)
        if (!frEdit) {
            await this.handleClearFaixaRating2()
            this.app.notify('error', 'Faixa Rating 2 que está tentando editar não encontrada')
            return
        }
        this.scope.editingFaixaRating2Id = frEdit.id
        this.scope.editingFaixaRating2Tipo = frEdit.tipo
        this.scope.editingFaixaRating2Sigla = frEdit.sigla
        this.scope.editingFaixaRating2Descricao = frEdit.descricao ?? ''
        this.scope.editingFaixaRating2FaixaInicial = frEdit.faixaInicial ?? 0
        this.scope.editingFaixaRating2FaixaFinal = frEdit.faixaFinal ?? 0
        this.scope.editingFaixaRating2MostrarRating = frEdit.mostrarRating
    }

    private async handleClearFaixaRating1() {
        this.scope.editingFaixaRating1Id = undefined
        this.scope.editingFaixaRating1Tipo = 'RATING_1'
        this.scope.editingFaixaRating1FaixaInicial = 0
        this.scope.editingFaixaRating1FaixaFinal = 0
        this.scope.editingFaixaRating1Sigla = ''
        this.scope.editingFaixaRating1Descricao = ''
        this.scope.editingFaixaRating1MostrarRating = true
    }

    private async handleClearFaixaRating2() {
        this.scope.editingFaixaRating2Id = undefined
        this.scope.editingFaixaRating2Tipo = 'RATING_2'
        this.scope.editingFaixaRating2FaixaInicial = 0
        this.scope.editingFaixaRating2FaixaFinal = 0
        this.scope.editingFaixaRating2Sigla = ''
        this.scope.editingFaixaRating2Descricao = ''
        this.scope.editingFaixaRating2MostrarRating = true
    }

    private async handleSaveFaixaRating1() {
        try {
            const faixaRating = {
                id: this.scope.editingFaixaRating1Id,
                tipo: this.scope.editingFaixaRating1Tipo ?? 'RATING_1',
                faixaInicial: this.scope.editingFaixaRating1FaixaInicial,
                faixaFinal: this.scope.editingFaixaRating1FaixaFinal,
                sigla: this.scope.editingFaixaRating1Sigla,
                descricao: this.scope.editingFaixaRating1Descricao,
                mostrarRating: this.scope.editingFaixaRating1MostrarRating
            } as FaixaRatingRow

            faixaRating.mostrarRating === undefined && (faixaRating.mostrarRating = false)

            if (this.scope.idSelected > 0) {
                const retSalvar =
                    faixaRating.id! > 0
                        ? await grupoService.updateFaixaRating(this.scope.idSelected, faixaRating.id!, faixaRating)
                        : await grupoService.saveFaixaRating(this.scope.idSelected, faixaRating)

                if (retSalvar) {
                    this.app.notify('success', 'Salvo com sucesso')
                    faixaRating.id = retSalvar.id
                }
            } else if (!faixaRating.id) {
                faixaRating.id = -Math.floor(Math.random() * 1000) as number
            }

            if (!this.scope.faixasRating1) this.scope.faixasRating1 = []

            if (!this.scope.faixasRating1.some((fr) => fr.id! === faixaRating.id!)) {
                this.scope.faixasRating1 = [...this.scope.faixasRating1, faixaRating]
            } else {
                this.scope.faixasRating1 = this.scope.faixasRating1.map((row) =>
                    row.id === faixaRating.id ? faixaRating : row
                )
            }
            await this.handleClearFaixaRating1()
        } catch (caught) {
            LOG.error('handleSaveFaixaRating1', caught)
            throw caught
        }
    }

    private async handleSaveFaixaRating2() {
        try {
            const faixaRating = {
                id: this.scope.editingFaixaRating2Id,
                tipo: this.scope.editingFaixaRating2Tipo ?? 'RATING_2',
                faixaInicial: this.scope.editingFaixaRating2FaixaInicial,
                faixaFinal: this.scope.editingFaixaRating2FaixaFinal,
                sigla: this.scope.editingFaixaRating2Sigla,
                descricao: this.scope.editingFaixaRating2Descricao,
                mostrarRating: this.scope.editingFaixaRating2MostrarRating
            } as FaixaRatingRow

            faixaRating.mostrarRating === undefined && (faixaRating.mostrarRating = false)

            if (this.scope.idSelected > 0) {
                const retSalvar =
                    faixaRating.id! > 0
                        ? await grupoService.updateFaixaRating(this.scope.idSelected, faixaRating.id!, faixaRating)
                        : await grupoService.saveFaixaRating(this.scope.idSelected, faixaRating)

                if (retSalvar) {
                    this.app.notify('success', 'Salvo com sucesso')
                    faixaRating.id = retSalvar.id
                }
            } else if (!faixaRating.id) {
                faixaRating.id = -Math.floor(Math.random() * 1000) as number
            }

            if (!this.scope.faixasRating2) this.scope.faixasRating2 = []

            if (!this.scope.faixasRating2.some((fr) => fr.id! === faixaRating.id!)) {
                this.scope.faixasRating2 = [...this.scope.faixasRating2, faixaRating]
            } else {
                this.scope.faixasRating2 = this.scope.faixasRating2.map((row) =>
                    row.id === faixaRating.id ? faixaRating : row
                )
            }
            await this.handleClearFaixaRating2()
        } catch (caught) {
            LOG.error('handleSaveFaixaRating2', caught)
            throw caught
        }
    }

    private async handleDeleteFaixaRating(frId: number) {
        try {
            if (!confirm('Confirma a exclusão da faixa de rating?')) return

            if (
                this.scope.idSelected > 0 &&
                frId > 0 &&
                (await grupoService.removeFaixaRating(this.scope.idSelected, frId))
            ) {
                this.app.notify('success', 'Removido com sucesso')
            }

            this.scope.faixasRating1 = this.scope.faixasRating1.filter((fr) => Number(fr.id) !== frId)
            this.scope.faixasRating2 = this.scope.faixasRating2.filter((fr) => Number(fr.id) !== frId)
        } catch (caught) {
            LOG.error('handleSaveFaixaRating2', caught)
            throw caught
        }
    }

    private async handleCreateAnalise() {
        await new AnaliseFormKeys(this.app).flip()
    }

    private async handleEditAnalise() {
        await new AnaliseFormKeys(this.app).flip()
    }

    private async handleAddAnalise() {
        const key = new AnaliseSelectKeys(this.app)
        key.grupoId = this.#grupoId
        await key.flip()
    }

    private async handleDeleteAnalise() {
        if (this.scope.idAnaliseSelected <= 0 || !confirm('Confirma a exclusão da análise?')) return
        await grupoService.removeGrupoAnalise(this.#grupoId!, this.scope.idAnaliseSelected)
        this.buscarGrupoSelecionado()
        this.app.notify('success', 'Removido com sucesso')
    }
}

export default GrupoFormPresenter
